import React, { useState } from 'react';
import { SmartDataTable } from '@src/components/SmartDataTable';
import { useInsideStockItemsColumns } from './hooks/useInsideStockItemsColumns';
import { useInsideStockItemsFormFields } from './hooks/useInsideStockItemsFormFields';
import { StockMovementForm } from './StockMovementForm';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useInvalidate } from '@refinedev/core';

export const InsideStockItemsPage = () => {
  const [showStockForm, setShowStockForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [transactionType, setTransactionType] = useState<'PURCHASE' | 'USAGE'>(
    'PURCHASE'
  );

  const columns = useInsideStockItemsColumns({ showCreatedAt: true }); // You can set this to false to hide the column
  const formFields = useInsideStockItemsFormFields();
  const invalidate = useInvalidate();

  // Dynamic edit form configuration
  const dynamicEditForm = {
    getForm: (record: any) => {
      return {
        fields: formFields(record),
        title: 'Edit Item',
        submitText: 'Save Changes',
      };
    },
    getResource: (record: any) => {
      return 'inside-inventory-items';
    },
  };

  // Custom actions for the table
  const customActions = [
    {
      key: 'add_stock',
      icon: <PlusOutlined />,
      label: '',
      onClick: (record: any) => {
        (window as any).showAddStock(record);
      },
      type: 'default' as const,
      tooltip: 'Add Stock',
    },
    {
      key: 'usage_stock',
      icon: <MinusOutlined />,
      label: '',
      onClick: (record: any) => {
        (window as any).showUsageStock(record);
      },
      type: 'default' as const,
      tooltip: 'Usage Stock',
    },
  ];

  // Add global functions to show stock form with different transaction types
  React.useEffect(() => {
    (window as any).showStockMovements = (item: any) => {
      setSelectedItem(item);
      setShowStockForm(true);
    };

    (window as any).showAddStock = (item: any) => {
      setSelectedItem(item);
      setTransactionType('PURCHASE');
      setShowStockForm(true);
    };

    (window as any).showUsageStock = (item: any) => {
      setSelectedItem(item);
      setTransactionType('USAGE');
      setShowStockForm(true);
    };

    return () => {
      delete (window as any).showStockMovements;
      delete (window as any).showAddStock;
      delete (window as any).showUsageStock;
    };
  }, []);

  // Handle stock movement success
  const handleStockMovementSuccess = () => {
    // Invalidate the table data to refresh it with the updated stock
    invalidate({
      resource: 'inside-inventory-items',
      invalidates: ['list'],
    });
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SmartDataTable
        api={{
          resource: 'inside-inventory-items',
        }}
        title="Inside Stock Items"
        columns={columns}
        search={{
          enabled: true,
          fields: ['name', 'category', 'unit'],
          placeholder: 'Search by item name, category, or unit...',
          debounceMs: 500,
        }}
        createForm={{
          fields: formFields() as any,
          title: 'Add New Item',
          submitText: 'Create Item',
        }}
        dynamicEditForm={dynamicEditForm}
        formDisplayMode="modal"
        showCreate={true}
        showEdit={true}
        showDelete={true}
        showView={false}
        pageSize={10}
        pageOptions={[10, 20, 50, 100]}
        showSizeChanger={true}
        showQuickJumper={false}
        // showExport={true}
        showColumnVisibility={true}
        tableProps={{
          scroll: {
            x: 'max-content',
            y: 'calc(100vh - 300px)',
          },
        }}
        rowKey="id"
        customActions={customActions}
      />

      {showStockForm && selectedItem && (
        <StockMovementForm
          visible={showStockForm}
          onCancel={() => {
            setShowStockForm(false);
            setSelectedItem(null);
          }}
          onSuccess={handleStockMovementSuccess}
          defaultTransactionType={transactionType}
          item={selectedItem}
        />
      )}
    </div>
  );
};
